/**
 * ChatInputBar Component
 * 
 * Message input with camera button, image preview, and send button.
 * Handles keyboard shortcuts (Enter to send).
 * Integrated with ChatImageUpload for inline image attachment.
 * 
 * Extracted from Figma: ORO - Nightfall Kit - ALL Pages
 * Reference: docs/design/FIGMA_IMPLEMENTATION_PLAN.md - Phase 7A
 */

import { Send, X, Loader } from 'lucide-react';
import { useState, useRef } from 'react';
import ChatImageUpload from './ChatImageUpload';
import { getZIndexClass } from '../config/zIndex';

interface UploadedImage {
  id: string;
  url: string;
  file: File;
  isUploading: boolean;
}

interface ChatInputBarProps {
  onSendMessage?: (message: string) => void;
  attachedImages?: UploadedImage[];
  onAttachedImagesChange?: (images: UploadedImage[] | ((prev: UploadedImage[]) => UploadedImage[])) => void;
  onRemoveImage?: (imageId: string) => void;
  clubId?: string;
  isSending?: boolean;
  containerRef?: React.Ref<HTMLDivElement>;
}

export default function ChatInputBar({ 
  onSendMessage,
  attachedImages = [],
  onAttachedImagesChange,
  onRemoveImage,
  clubId,
  isSending = false,
  containerRef
}: ChatInputBarProps) {
  const [message, setMessage] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const isUploading = attachedImages.some(img => img.isUploading);
  const canSend = (message.trim() || attachedImages.length > 0) && !isUploading && !isSending;

  const handleSend = () => {
    if (canSend && onSendMessage) {
      onSendMessage(message.trim());
      setMessage('');
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div 
      ref={containerRef}
      className={`fixed inset-x-0 px-4 py-3 border-t border-oro-border bg-oro-surface ${getZIndexClass('chatInput')}`}
      style={{
        bottom: 'calc(max(var(--oro-bottom-nav-height, 64px), 64px) + max(env(safe-area-inset-bottom), 0px))',
      }}
    >
      {/* Image preview section */}
      {attachedImages.length > 0 && (
        <div className="mb-3 flex gap-2 overflow-x-auto">
          {attachedImages.map((image) => (
            <div key={image.id} className="relative shrink-0">
              <img
                src={image.url}
                alt=""
                className="w-16 h-16 rounded-lg object-cover border border-oro-border"
              />
              {image.isUploading && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg">
                  <Loader className="w-4 h-4 animate-spin text-accent" />
                </div>
              )}
              <button
                onClick={() => onRemoveImage?.(image.id)}
                className="absolute -top-1 -right-1 w-5 h-5 bg-oro-error rounded-full flex items-center justify-center text-white text-xs hover:bg-oro-error/80 transition-colors"
                aria-label="Remove image"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      
      <div className="flex items-center gap-3">
        {/* Camera button with integrated upload */}
        {clubId && onAttachedImagesChange && (
          <ChatImageUpload
            clubId={clubId}
            images={attachedImages}
            onImagesChange={onAttachedImagesChange}
            maxImages={4}
            disabled={attachedImages.length >= 4}
          />
        )}

        {/* Text input */}
        <div className="flex-1">
          <input
            ref={inputRef}
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type a message..."
            disabled={isSending}
            className="w-full h-11 px-4 rounded-full outline-none transition-all bg-oro-surface-alt text-text-primary border border-transparent focus:border-2 focus:border-accent shadow-oro-soft disabled:opacity-50"
          />
        </div>

        {/* Send button */}
        <button
          onClick={handleSend}
          disabled={!canSend}
          className="shrink-0 w-11 h-11 rounded-full flex items-center justify-center transition-all hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed bg-accent border-0 shadow-oro-soft"
          aria-label="Send message"
        >
          {isSending ? (
            <Loader className="w-5 h-5 text-oro-bg animate-spin" />
          ) : (
            <Send className="w-5 h-5 text-oro-bg" />
          )}
        </button>
      </div>
    </div>
  );
}
